import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import {
  ConnectionObjectMapApiProvider,
  useConnectionObjectMapApi,
} from '../Services/ConnectionObjectMapApiProvider';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

function ConnectionsObjectMap({ canEdit }) {
  const { getConnectionObjectMap, deleteConnectionObjectMap } = useConnectionObjectMapApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionObjectMaps, setConnectionObjectMaps] = useState([]);
  const [totalConnectionObjectMaps, setTotalConnectionObjectMaps] = useState(0);
  const navigate = useNavigate();

  const tableTitle = 'Connection Object Maps';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Connection name', selector: (row) => row.connection_name, sortable: true },
    { name: 'Required', selector: (row) => (row.is_required ? 'Yes' : 'No'), sortable: true },
    { name: 'Active', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Default Value', selector: (row) => row.default_value, sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const fetchConnectionObjectMaps = async (skip, limit) => {
    try {
      setLoading(true);
      const response = await getConnectionObjectMap(skip, limit);
      setConnectionObjectMaps(response.items);
      setTotalConnectionObjectMaps(response.totalCount);
    } catch (error) {
      console.error('Error fetching connections:', error);
      setError('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionObjectMaps(persistedSkip, persistedPageSize);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/connection-object-map/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete the selected Connection?`, async () => {
      try {
        setLoading(true);
        await deleteConnectionObjectMap(selectedId);
        fetchConnectionObjectMaps(persistedSkip, persistedPageSize);
        showAlert('success', 'Connection was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the connection!');
      }
    });
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={connectionObjectMaps}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalConnectionObjectMaps}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchConnectionObjectMaps}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/connection-object-map/new-connection-object-map`,
                    )
                  }
                >
                  Create New Connection Object Map
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectionObjectMapWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ConnectionObjectMapApiProvider>
        <ConnectionsObjectMap canEdit={canEdit} />
      </ConnectionObjectMapApiProvider>
    </ConfirmationProvider>
  );
}

export default ConnectionObjectMapWrapper;
